import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MoreHorizontal, Eye, Edit, Trash2, Phone, Share2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const STATUS_COLORS = {
  new: { bg: 'bg-blue-500', light: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
  contacted: { bg: 'bg-slate-500', light: 'bg-slate-50 border-slate-200', text: 'text-slate-700' },
  qualified: { bg: 'bg-violet-500', light: 'bg-violet-50 border-violet-200', text: 'text-violet-700' },
  proposal: { bg: 'bg-amber-500', light: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
  negotiation: { bg: 'bg-orange-500', light: 'bg-orange-50 border-orange-200', text: 'text-orange-700' },
  converted: { bg: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  lost: { bg: 'bg-red-500', light: 'bg-red-50 border-red-200', text: 'text-red-700' },
};

const STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'converted', 'lost'];

function KanbanCard({ lead, isViewer, isDragging, onDragStart, onDragEnd, onEdit, onShare, onDelete }) {
  return (
    <div
      draggable={!isViewer}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`border border-slate-200 rounded-sm p-2.5 bg-white hover:border-slate-300 hover:shadow-sm transition-all duration-150 ${!isViewer ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragging ? 'opacity-40' : ''}`}
      data-testid={`kanban-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <Link to={`/leads/${lead.id}`} className="text-xs font-semibold text-slate-900 hover:text-[#0047AB] truncate block">{lead.name}</Link>
          {lead.company && <p className="text-[10px] text-slate-500 truncate mt-0.5">{lead.company}</p>}
        </div>
        {!isViewer && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 shrink-0"><MoreHorizontal className="w-3 h-3" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem asChild><Link to={`/leads/${lead.id}`} className="text-xs"><Eye className="w-3 h-3 mr-1.5" /> View</Link></DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(lead)} className="text-xs"><Edit className="w-3 h-3 mr-1.5" /> Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onShare(lead)} className="text-xs"><Share2 className="w-3 h-3 mr-1.5" /> Share</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(lead.id)} className="text-xs text-red-600"><Trash2 className="w-3 h-3 mr-1.5" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        {lead.category && <span className="text-[9px] font-medium px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{lead.category}</span>}
        {lead.phone && <a href={`tel:${lead.phone}`} className="text-[9px] text-[#0047AB] flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" /></a>}
        {lead.shared_with?.length > 0 && <Share2 className="w-2.5 h-2.5 text-[#0047AB]" />}
      </div>
      {lead.tags?.length > 0 && (
        <div className="flex gap-1 mt-1.5 flex-wrap">
          {lead.tags.slice(0, 2).map(t => <span key={t} className="text-[8px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded-sm">{t}</span>)}
          {lead.tags.length > 2 && <span className="text-[8px] text-slate-400">+{lead.tags.length - 2}</span>}
        </div>
      )}
    </div>
  );
}

function KanbanColumn({ status, leads, isViewer, isOver, dragItemId, onDragOver, onDragLeave, onDrop, onDragStart, onDragEnd, onEdit, onShare, onDelete }) {
  const col = STATUS_COLORS[status];
  return (
    <div
      className={`flex-shrink-0 w-64 rounded-sm border transition-all duration-200 ${isOver ? `${col.light} shadow-md` : 'border-slate-200 bg-white'}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`kanban-col-${status}`}
    >
      <div className="p-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${col.bg}`} />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{status}</span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">{leads.length}</span>
      </div>
      <div className="p-2 space-y-2 min-h-[120px] max-h-[60vh] overflow-y-auto">
        {leads.map(lead => (
          <KanbanCard
            key={lead.id} lead={lead} isViewer={isViewer}
            isDragging={dragItemId === lead.id}
            onDragStart={(e) => onDragStart(e, lead)}
            onDragEnd={onDragEnd}
            onEdit={onEdit} onShare={onShare} onDelete={onDelete}
          />
        ))}
        {leads.length === 0 && (
          <div className="text-center py-6"><p className="text-[10px] text-slate-300">Drop leads here</p></div>
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({ leads, onRefresh, onEdit, onShare, isViewer }) {
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const handleDragStart = (e, lead) => {
    setDragItem(lead);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragItem || dragItem.status === newStatus || isViewer) return;
    try {
      await api.updateLead(dragItem.id, { status: newStatus });
      toast.success(`Moved "${dragItem.name}" to ${newStatus}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move lead');
    }
    setDragItem(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await api.deleteLead(id); toast.success('Deleted'); onRefresh(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
      {STATUSES.map(status => (
        <KanbanColumn
          key={status} status={status}
          leads={leads.filter(l => l.status === status)}
          isViewer={isViewer}
          isOver={dragOver === status}
          dragItemId={dragItem?.id}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(status); }}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDrop(e, status)}
          onDragStart={handleDragStart}
          onDragEnd={() => setDragItem(null)}
          onEdit={onEdit} onShare={onShare} onDelete={handleDelete}
        />
      ))}
    </div>
  );
}
